import { Component, type ErrorInfo, type ReactNode } from "react";
import { IconBrandDiscord } from "@tabler/icons-react";
import commissionedImage from "../assets/nif-logo-512.png";
import commissionedImage384 from "../assets/nif-logo-384.png";
import commissionedImage256 from "../assets/nif-logo-256.png";

type Props = {
  children: ReactNode;
};

type State = {
  hasError: boolean;
  errorMessage: string | null;
};

export class NifusionErrorBoundary extends Component<Props, State> {
  state: State = {
    hasError: false,
    errorMessage: null,
  };

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      errorMessage: error.message || "Unknown error",
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("NifusionErrorBoundary caught:", error, errorInfo);
  }

  private handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <div className="flex min-h-dvh items-center justify-center bg-[color:var(--color-bg)] px-4 py-10 text-[color:var(--color-fg)]">
        <div className="panel w-full max-w-lg p-6 text-center">
          <img
            src={commissionedImage}
            srcSet={`${commissionedImage256} 256w, ${commissionedImage384} 384w, ${commissionedImage} 512w`}
            sizes="(max-width: 480px) 70vw, (max-width: 768px) 40vw, 512px"
            alt="Nifusion commissioned artwork"
            loading="lazy"
            decoding="async"
            fetchPriority="low"
            className="mx-auto h-48 w-auto rounded-lg object-contain"
          />

          <h2 className="mt-4 text-2xl font-bold tracking-tight">
            Tiny crash, big yawn
          </h2>
          <p className="mt-2 text-sm text-[color:var(--color-fg-muted)]">
            Something nodded off mid-request. Give it a wake-up and try again.
          </p>

          <div className="mt-4 rounded-lg border border-[color:var(--color-danger)]/30 bg-[color:var(--color-danger)]/10 p-3 text-left text-sm text-[color:var(--color-fg-muted)]">
            <span className="font-medium text-[color:var(--color-danger)]">
              What went wrong:
            </span>{" "}
            <span className="break-words">
              {this.state.errorMessage || "An unexpected error."}
            </span>
          </div>

          <div className="mt-5 flex flex-wrap justify-center gap-2">
            <button
              type="button"
              onClick={this.handleReload}
              className="inline-flex items-center gap-2 rounded-full bg-[color:var(--color-accent)] px-4 py-2 text-sm font-semibold text-[color:var(--color-bg)] transition-colors hover:bg-[color:var(--color-accent-strong)]"
            >
              Reload the page
            </button>
            <a
              href="discord://-/users/317065077268283392"
              className="inline-flex items-center gap-2 rounded-full border border-[color:var(--color-border)] px-4 py-2 text-sm font-semibold text-[color:var(--color-fg-muted)] transition-colors hover:border-[color:var(--color-border-strong)] hover:text-[color:var(--color-fg)]"
            >
              <IconBrandDiscord size={15} />
              Tell me on Discord
            </a>
          </div>
        </div>
      </div>
    );
  }
}
